import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { vertraegeApi } from '../api';

const AMPEL_STYLE = {
  dunkelrot: { bg: '#7f1d1d44', border: '#dc2626', dot: '#dc2626' },
  rot:       { bg: '#45090944', border: '#ef4444', dot: '#ef4444' },
  orange:    { bg: '#43140744', border: '#f97316', dot: '#f97316' },
  gelb:      { bg: '#42200644', border: '#eab308', dot: '#eab308' },
  gruen:     { bg: '#052e1644', border: '#22c55e', dot: '#22c55e' },
  grau:      { bg: '#1e293b',   border: '#334155', dot: '#475569' },
};

const STATUS_FILTER = [
  { id: 'alle', label: 'Alle' },
  { id: 'aktiv', label: 'Aktiv' },
  { id: 'laeuft_bald_aus', label: 'Bald aus' },
  { id: 'kuendigungsfrist_laeuft', label: 'Frist läuft' },
  { id: 'frist_verpasst', label: 'Frist verpasst' },
  { id: 'gekuendigt', label: 'Gekündigt' },
  { id: 'abgelaufen', label: 'Abgelaufen' },
];

const SORT_OPTIONS = [
  { id: 'titel', label: 'Name' },
  { id: 'deadline_tage', label: 'Dringlichkeit' },
  { id: 'kosten', label: 'Kosten' },
  { id: 'vertragsende', label: 'Vertragsende' },
];

const BG_PANEL = '#1e293b';
const BORDER = '#334155';
const TEXT_BODY = '#94a3b8';
const EMPTY_STATE_TEXT = { vertrage: 'Keine Verträge vorhanden', dokumente: 'Keine Dokumente vorhanden', kundigungen: 'Keine Kündigungen vorhanden' };

export default function VertraegePage() {
  const [vertraege, setVertraege] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('alle');
  const [dringlichkeitFilter, setDringlichkeitFilter] = useState('');
  const [autoRenewalFilter, setAutoRenewalFilter] = useState('');
  const [suche, setSuche] = useState('');
  const [sortBy, setSortBy] = useState('deadline_tage');
  const [sortDesc, setSortDesc] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const api = vertraegeApi.uebersicht
      ? vertraegeApi.uebersicht()
      : vertraegeApi.list();
    api
      .then(res => {
        const data = res.data;
        setVertraege(Array.isArray(data) ? data : (data.vertraege || []));
      })
      .finally(() => setLoading(false));
  }, []);

  const gefiltert = vertraege.filter(v => {
    const matchStatus = statusFilter === 'alle' || v.status === statusFilter || v.berechneter_status === statusFilter;
    const matchDring = !dringlichkeitFilter || v.dringlichkeit === dringlichkeitFilter;
    const matchAuto = !autoRenewalFilter || String(v.auto_renewal) === autoRenewalFilter;
    const matchSuche = !suche ||
      (v.titel || '').toLowerCase().includes(suche.toLowerCase()) ||
      (v.anbieter || '').toLowerCase().includes(suche.toLowerCase()) ||
      (v.kategorie_name || '').toLowerCase().includes(suche.toLowerCase());
    return matchStatus && matchDring && matchAuto && matchSuche;
  }).sort((a, b) => {
    let av = a[sortBy], bv = b[sortBy];
    if (sortBy === 'deadline_tage') {
      av = av !== null && av !== undefined ? Number(av) : 99999;
      bv = bv !== null && bv !== undefined ? Number(bv) : 99999;
    } else if (sortBy === 'kosten') {
      av = Number(av) || 0; bv = Number(bv) || 0;
    } else {
      av = String(av || ''); bv = String(bv || '');
    }
    const dir = sortDesc ? -1 : 1;
    return av < bv ? -dir : av > bv ? dir : 0;
  });

  const activeFilters = statusFilter !== 'alle' || dringlichkeitFilter || autoRenewalFilter || suche;

  if (loading) return <div style={{ padding: 20, color: '#64748b', textAlign: 'center' }}>⏳ Laden...</div>;
  if (vertraege.length === 0) {
    return (
      <div style={{ backgroundColor: BG_PANEL, border: `1px solid ${BORDER}`, color: TEXT_BODY, padding: '2rem', textAlign: 'center', margin: '2rem' }}>
        {EMPTY_STATE_TEXT.vertrage}
      </div>
    );
  }

  return (
    <div style={{ padding: '16px', maxWidth: 720, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ color: '#f1f5f9', fontSize: 20, fontWeight: 700, margin: 0 }}>📋 Verträge</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => navigate('/vertraege/scan')} style={actionBtn('#10b981')}>🤖 KI-Scan</button>
          <button onClick={() => navigate('/vertraege/neu')} className="btn-primary">+ Neu</button>
        </div>
      </div>

      <input
        value={suche} onChange={e => setSuche(e.target.value)}
        placeholder="🔍  Suchen nach Name, Anbieter, Kategorie..."
        style={{ width: '100%', background: '#1e293b', border: '1px solid #334155',
          color: '#f1f5f9', padding: '10px 14px', borderRadius: 8, fontSize: 14,
          marginBottom: 12, outline: 'none', boxSizing: 'border-box' }}
      />

      {/* Status-Filter */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        {STATUS_FILTER.map(f => (
          <button key={f.id} onClick={() => setStatusFilter(f.id)} style={filterBtn(statusFilter === f.id)}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Erweiterte Filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={dringlichkeitFilter} onChange={e => setDringlichkeitFilter(e.target.value)}
          style={selectStyle}>
          <option value="">Alle Dringlichkeiten</option>
          <option value="kritisch">Kritisch</option>
          <option value="hoch">Hoch</option>
          <option value="mittel">Mittel</option>
          <option value="niedrig">Niedrig</option>
        </select>
        <select value={autoRenewalFilter} onChange={e => setAutoRenewalFilter(e.target.value)}
          style={selectStyle}>
          <option value="">Auto-Verlängerung: alle</option>
          <option value="1">Mit Auto-Verlängerung</option>
          <option value="0">Ohne Auto-Verlängerung</option>
        </select>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={selectStyle}>
          {SORT_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
        <button onClick={() => setSortDesc(d => !d)} style={{ ...selectStyle, cursor: 'pointer', padding: '6px 10px' }}
          title="Sortierrichtung umkehren">
          {sortDesc ? '↑' : '↓'}
        </button>
        {activeFilters && (
          <button onClick={() => { setStatusFilter('alle'); setDringlichkeitFilter(''); setAutoRenewalFilter(''); setSuche(''); }}
            className="btn-secondary">
            Filter zurücksetzen
          </button>
        )}
      </div>

      <div style={{ color: '#64748b', fontSize: 12, marginBottom: 10 }}>
        {gefiltert.length} von {vertraege.length} Verträgen
      </div>

      {gefiltert.map(v => <VertragCard key={v.id} vertrag={v} onClick={() => navigate(`/vertraege/${v.id}`)} />)}
    </div>
  );
}

function VertragCard({ vertrag: v, onClick }) {
  const ampelKey = v.ampel || 'grau';
  const ampel = AMPEL_STYLE[ampelKey] || AMPEL_STYLE.grau;
  const deadlineTage = v.deadline_tage !== null && v.deadline_tage !== undefined ? Number(v.deadline_tage) : null;

  return (
    <div onClick={onClick} style={{ background: '#1e293b', borderRadius: 12, padding: '14px 16px',
      marginBottom: 10, cursor: 'pointer', border: `1px solid ${ampel.border}`,
      boxShadow: ampelKey !== 'grau' ? `0 0 0 1px ${ampel.border}22` : 'none' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: ampel.dot, flex: '0 0 auto' }} aria-hidden="true" />
            <span style={{ fontSize: 18, flex: '0 0 auto' }}>{v.kategorie_icon || '📄'}</span>
            <span style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.titel}</span>
          </div>
          {v.anbieter && <div style={{ color: '#64748b', fontSize: 12, marginLeft: 32 }}>{v.anbieter}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', marginLeft: 0 }}>
            {v.kosten > 0 && (
              <span style={{ background: '#0f172a', color: '#10b981', padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
                {formatEuro(v.kosten)}/{zahlungsKurz(v.zahlungsintervall)}
              </span>
            )}
            {v.kategorie_name && (
              <span style={{ background: '#0f172a', color: '#94a3b8', padding: '2px 8px', borderRadius: 12, fontSize: 12 }}>
                {v.kategorie_name}
              </span>
            )}
            {v.auto_renewal == 1 && (
              <span style={{ background: '#1e1b4b', color: '#818cf8', padding: '2px 8px', borderRadius: 12, fontSize: 12 }}>
                🔄 Auto
              </span>
            )}
            {v.dokument_anzahl > 0 && (
              <span style={{ background: '#0f172a', color: '#64748b', padding: '2px 8px', borderRadius: 12, fontSize: 12 }}>
                📎 {v.dokument_anzahl}
              </span>
            )}
          </div>
        </div>
        <div style={{ textAlign: 'right', flex: '0 0 auto', minWidth: 80 }}>
          {deadlineTage !== null && (
            <div style={{ color: ampel.dot, fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
              {deadlineTage < 0 ? '⏰ Überfällig!' : deadlineTage === 0 ? '⏰ Heute!' : `⏰ ${deadlineTage}d`}
            </div>
          )}
          <div style={{ fontSize: 11, padding: '4px 8px', borderRadius: 4,
            background: `${ampel.dot}22`, color: ampel.dot, fontWeight: 600 }}>
            {v.berechneter_status || v.status}
          </div>
          {v.vertragsende && (
            <div style={{ color: '#475569', fontSize: 11, marginTop: 4 }}>
              Ende: {formatDE(v.vertragsende)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatEuro(v) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
}
function zahlungsKurz(i) {
  return { monatlich: 'Mo', jaehrlich: 'Jahr', vierteljaehrlich: 'Qu', halbjaehrlich: 'Hj' }[i] || i || '';
}
function formatDE(d) {
  if (!d) return '–';
  const [y, m, day] = d.split('-');
  return `${day}.${m}.${y}`;
}

const actionBtn = (bg) => ({
  background: bg, color: 'white', border: 'none', padding: '8px 14px',
  borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
});
const filterBtn = (active) => ({
  background: active ? '#818cf8' : '#1e293b',
  color: active ? 'white' : '#94a3b8',
  border: '1px solid #334155', padding: '5px 12px',
  borderRadius: 20, cursor: 'pointer', fontSize: 12,
});
const selectStyle = {
  background: '#1e293b', color: '#94a3b8', border: '1px solid #334155',
  borderRadius: 8, padding: '6px 8px', fontSize: 12,
};
